import React, { useState } from 'react';
import { Calculator, RotateCw, Drill, Grid3x3, Minus } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { motion } from 'framer-motion';

const CuttingCalculator = () => {
  // Turning Calculator State
  const [turningInputs, setTurningInputs] = useState({
    diameter: '',
    cuttingSpeed: '',
    feedPerRev: ''
  });
  const [turningResults, setTurningResults] = useState(null);

  // Milling Calculator State
  const [millingInputs, setMillingInputs] = useState({
    diameter: '',
    cuttingSpeed: '',
    teethCount: '',
    feedPerTooth: ''
  });
  const [millingResults, setMillingResults] = useState(null);

  // Drilling Calculator State
  const [drillingInputs, setDrillingInputs] = useState({
    diameter: '',
    cuttingSpeed: '',
    feedPerRev: ''
  });
  const [drillingResults, setDrillingResults] = useState(null);

  // Grooving Calculator State
  const [groovingInputs, setGroovingInputs] = useState({
    diameter: '',
    cuttingSpeed: '',
    feedPerRev: ''
  });
  const [groovingResults, setGroovingResults] = useState(null);

  // Turning Calculations
  const calculateTurning = () => {
    const d = parseFloat(turningInputs.diameter);
    const vc = parseFloat(turningInputs.cuttingSpeed);
    const f = parseFloat(turningInputs.feedPerRev);

    if (!d || !vc) return;

    // Spindle Speed (RPM) = (Vc × 1000) / (π × D)
    const spindleSpeed = (vc * 1000) / (Math.PI * d);
    
    // Feed Rate (mm/min) = f × n
    const feedRate = f ? f * spindleSpeed : null;

    // Material Removal Rate (cm³/min) = Vc × ap × f
    // Simplified for basic calculation
    const timePerPass = d ? (Math.PI * d) / (vc * 1000) : null;

    setTurningResults({
      spindleSpeed: spindleSpeed.toFixed(0),
      feedRate: feedRate ? feedRate.toFixed(1) : '-',
      timePerPass: timePerPass ? (timePerPass * 60).toFixed(2) : '-'
    });
  };

  // Milling Calculations
  const calculateMilling = () => {
    const d = parseFloat(millingInputs.diameter);
    const vc = parseFloat(millingInputs.cuttingSpeed);
    const z = parseFloat(millingInputs.teethCount);
    const fz = parseFloat(millingInputs.feedPerTooth);

    if (!d || !vc) return;

    // Spindle Speed (RPM) = (Vc × 1000) / (π × D)
    const spindleSpeed = (vc * 1000) / (Math.PI * d);
    
    // Table Feed (mm/min) = fz × z × n
    const tableFeed = (z && fz) ? fz * z * spindleSpeed : null;

    setMillingResults({
      spindleSpeed: spindleSpeed.toFixed(0),
      tableFeed: tableFeed ? tableFeed.toFixed(1) : '-'
    });
  };

  // Drilling Calculations
  const calculateDrilling = () => {
    const d = parseFloat(drillingInputs.diameter);
    const vc = parseFloat(drillingInputs.cuttingSpeed);
    const f = parseFloat(drillingInputs.feedPerRev);

    if (!d || !vc) return;

    // Spindle Speed (RPM) = (Vc × 1000) / (π × D)
    const spindleSpeed = (vc * 1000) / (Math.PI * d);
    
    // Feed Rate (mm/min) = f × n
    const feedRate = f ? f * spindleSpeed : null;

    setDrillingResults({
      spindleSpeed: spindleSpeed.toFixed(0),
      feedRate: feedRate ? feedRate.toFixed(1) : '-'
    });
  };

  // Grooving Calculations
  const calculateGrooving = () => {
    const d = parseFloat(groovingInputs.diameter);
    const vc = parseFloat(groovingInputs.cuttingSpeed);
    const f = parseFloat(groovingInputs.feedPerRev);

    if (!d || !vc) return;

    // Spindle Speed (RPM) = (Vc × 1000) / (π × D)
    const spindleSpeed = (vc * 1000) / (Math.PI * d);
    
    // Feed Rate (mm/min) = f × n
    const feedRate = f ? f * spindleSpeed : null;

    setGroovingResults({
      spindleSpeed: spindleSpeed.toFixed(0),
      feedRate: feedRate ? feedRate.toFixed(1) : '-'
    });
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#f8f9fb] to-white">
      {/* Header */}
      <section className="bg-[#2E3A6F] text-white py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="max-w-3xl mx-auto text-center space-y-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-white/10 mb-4">
              <Calculator size={32} />
            </div>
            <h1 className="text-4xl sm:text-5xl font-bold">Cutting Tools Calculator</h1>
            <p className="text-lg text-white/90">
              Professional machining calculations for turning, milling, drilling, and grooving operations
            </p>
          </motion.div>
        </div>
      </section>

      {/* Calculator Section */}
      <section className="py-16">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-5xl mx-auto">
            <Tabs defaultValue="turning" className="w-full">
              <TabsList className="grid w-full grid-cols-4 mb-8">
                <TabsTrigger value="turning" className="flex items-center gap-2">
                  <RotateCw size={18} />
                  <span className="hidden sm:inline">Turning</span>
                </TabsTrigger>
                <TabsTrigger value="milling" className="flex items-center gap-2">
                  <Grid3x3 size={18} />
                  <span className="hidden sm:inline">Milling</span>
                </TabsTrigger>
                <TabsTrigger value="drilling" className="flex items-center gap-2">
                  <Drill size={18} />
                  <span className="hidden sm:inline">Drilling</span>
                </TabsTrigger>
                <TabsTrigger value="grooving" className="flex items-center gap-2">
                  <Minus size={18} />
                  <span className="hidden sm:inline">Grooving</span>
                </TabsTrigger>
              </TabsList>

              {/* Turning Calculator */}
              <TabsContent value="turning">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <RotateCw className="text-[#2E3A6F]" />
                      Turning Calculator
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="turn-diameter">Workpiece Diameter (mm)</Label>
                        <Input
                          id="turn-diameter"
                          type="number"
                          placeholder="e.g., 50"
                          value={turningInputs.diameter}
                          onChange={(e) => setTurningInputs({...turningInputs, diameter: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="turn-vc">Cutting Speed Vc (m/min)</Label>
                        <Input
                          id="turn-vc"
                          type="number"
                          placeholder="e.g., 250"
                          value={turningInputs.cuttingSpeed}
                          onChange={(e) => setTurningInputs({...turningInputs, cuttingSpeed: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="turn-feed">Feed per Rev f (mm/rev)</Label>
                        <Input
                          id="turn-feed"
                          type="number"
                          step="0.01"
                          placeholder="e.g., 0.3"
                          value={turningInputs.feedPerRev}
                          onChange={(e) => setTurningInputs({...turningInputs, feedPerRev: e.target.value})}
                        />
                      </div>
                    </div>

                    <Button onClick={calculateTurning} className="w-full bg-[#2E3A6F] hover:bg-[#1f2747]">
                      Calculate
                    </Button>

                    {turningResults && (
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-[#2E3A6F]/5 rounded-lg p-6 space-y-4"
                      >
                        <h3 className="font-semibold text-lg text-[#2E3A6F]">Results:</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                          <div>
                            <p className="text-sm text-gray-600">Spindle Speed (n)</p>
                            <p className="text-2xl font-bold text-[#2E3A6F]">{turningResults.spindleSpeed} RPM</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">Feed Rate (Vf)</p>
                            <p className="text-2xl font-bold text-[#2E3A6F]">{turningResults.feedRate} mm/min</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">Time per Pass</p>
                            <p className="text-2xl font-bold text-[#2E3A6F]">{turningResults.timePerPass} sec</p>
                          </div>
                        </div>
                      </motion.div>
                    )}

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm">
                      <p className="font-semibold mb-2">Recommended Cutting Speeds (Vc) for Turning:</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <div>
                          <p className="font-medium text-gray-800">Steel:</p>
                          <ul className="space-y-1 text-gray-700 ml-4">
                            <li>• Low Carbon: 200-300 m/min</li>
                            <li>• Medium Carbon: 150-250 m/min</li>
                            <li>• Stainless: 80-150 m/min</li>
                          </ul>
                        </div>
                        <div>
                          <p className="font-medium text-gray-800">Other Materials:</p>
                          <ul className="space-y-1 text-gray-700 ml-4">
                            <li>• Cast Iron: 150-300 m/min</li>
                            <li>• Aluminum: 400-800 m/min</li>
                            <li>• Brass: 200-400 m/min</li>
                          </ul>
                        </div>
                      </div>
                      <p className="text-xs text-gray-600 mt-2">Feed Rate: 0.1-0.5 mm/rev (finishing), 0.3-0.8 mm/rev (roughing)</p>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Milling Calculator */}
              <TabsContent value="milling">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Grid3x3 className="text-[#2E3A6F]" />
                      Milling Calculator
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="mill-diameter">Cutter Diameter (mm)</Label>
                        <Input
                          id="mill-diameter"
                          type="number"
                          placeholder="e.g., 80"
                          value={millingInputs.diameter}
                          onChange={(e) => setMillingInputs({...millingInputs, diameter: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="mill-vc">Cutting Speed Vc (m/min)</Label>
                        <Input
                          id="mill-vc"
                          type="number"
                          placeholder="e.g., 300"
                          value={millingInputs.cuttingSpeed}
                          onChange={(e) => setMillingInputs({...millingInputs, cuttingSpeed: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="mill-teeth">Number of Teeth (z)</Label>
                        <Input
                          id="mill-teeth"
                          type="number"
                          placeholder="e.g., 8"
                          value={millingInputs.teethCount}
                          onChange={(e) => setMillingInputs({...millingInputs, teethCount: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="mill-fz">Feed per Tooth fz (mm/tooth)</Label>
                        <Input
                          id="mill-fz"
                          type="number"
                          step="0.01"
                          placeholder="e.g., 0.15"
                          value={millingInputs.feedPerTooth}
                          onChange={(e) => setMillingInputs({...millingInputs, feedPerTooth: e.target.value})}
                        />
                      </div>
                    </div>

                    <Button onClick={calculateMilling} className="w-full bg-[#2E3A6F] hover:bg-[#1f2747]">
                      Calculate
                    </Button>

                    {millingResults && (
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-[#2E3A6F]/5 rounded-lg p-6 space-y-4"
                      >
                        <h3 className="font-semibold text-lg text-[#2E3A6F]">Results:</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <p className="text-sm text-gray-600">Spindle Speed (n)</p>
                            <p className="text-2xl font-bold text-[#2E3A6F]">{millingResults.spindleSpeed} RPM</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">Table Feed (Vf)</p>
                            <p className="text-2xl font-bold text-[#2E3A6F]">{millingResults.tableFeed} mm/min</p>
                          </div>
                        </div>
                      </motion.div>
                    )}

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm">
                      <p className="font-semibold mb-2">Recommended Cutting Speeds (Vc) for Milling:</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <div>
                          <p className="font-medium text-gray-800">Steel:</p>
                          <ul className="space-y-1 text-gray-700 ml-4">
                            <li>• Low Carbon: 250-400 m/min</li>
                            <li>• Medium Carbon: 200-300 m/min</li>
                            <li>• Stainless: 100-200 m/min</li>
                          </ul>
                        </div>
                        <div>
                          <p className="font-medium text-gray-800">Other Materials:</p>
                          <ul className="space-y-1 text-gray-700 ml-4">
                            <li>• Cast Iron: 200-400 m/min</li>
                            <li>• Aluminum: 600-1200 m/min</li>
                            <li>• Brass: 300-500 m/min</li>
                          </ul>
                        </div>
                      </div>
                      <p className="text-xs text-gray-600 mt-2">Feed per tooth (fz): 0.05-0.15 mm/tooth (finishing), 0.15-0.3 mm/tooth (roughing)</p>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Drilling Calculator */}
              <TabsContent value="drilling">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Drill className="text-[#2E3A6F]" />
                      Drilling Calculator
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="drill-diameter">Drill Diameter (mm)</Label>
                        <Input
                          id="drill-diameter"
                          type="number"
                          placeholder="e.g., 10"
                          value={drillingInputs.diameter}
                          onChange={(e) => setDrillingInputs({...drillingInputs, diameter: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="drill-vc">Cutting Speed Vc (m/min)</Label>
                        <Input
                          id="drill-vc"
                          type="number"
                          placeholder="e.g., 100"
                          value={drillingInputs.cuttingSpeed}
                          onChange={(e) => setDrillingInputs({...drillingInputs, cuttingSpeed: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="drill-feed">Feed per Rev f (mm/rev)</Label>
                        <Input
                          id="drill-feed"
                          type="number"
                          step="0.01"
                          placeholder="e.g., 0.2"
                          value={drillingInputs.feedPerRev}
                          onChange={(e) => setDrillingInputs({...drillingInputs, feedPerRev: e.target.value})}
                        />
                      </div>
                    </div>

                    <Button onClick={calculateDrilling} className="w-full bg-[#2E3A6F] hover:bg-[#1f2747]">
                      Calculate
                    </Button>

                    {drillingResults && (
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-[#2E3A6F]/5 rounded-lg p-6 space-y-4"
                      >
                        <h3 className="font-semibold text-lg text-[#2E3A6F]">Results:</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <p className="text-sm text-gray-600">Spindle Speed (n)</p>
                            <p className="text-2xl font-bold text-[#2E3A6F]">{drillingResults.spindleSpeed} RPM</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">Feed Rate (Vf)</p>
                            <p className="text-2xl font-bold text-[#2E3A6F]">{drillingResults.feedRate} mm/min</p>
                          </div>
                        </div>
                      </motion.div>
                    )}

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm">
                      <p className="font-semibold mb-2">Recommended Cutting Speeds (Vc) for Drilling:</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <div>
                          <p className="font-medium text-gray-800">Steel (HSS Drill):</p>
                          <ul className="space-y-1 text-gray-700 ml-4">
                            <li>• Low Carbon: 25-35 m/min</li>
                            <li>• Medium Carbon: 20-30 m/min</li>
                            <li>• Stainless: 10-20 m/min</li>
                          </ul>
                        </div>
                        <div>
                          <p className="font-medium text-gray-800">Carbide Drill:</p>
                          <ul className="space-y-1 text-gray-700 ml-4">
                            <li>• Steel: 80-150 m/min</li>
                            <li>• Cast Iron: 100-200 m/min</li>
                            <li>• Aluminum: 200-400 m/min</li>
                          </ul>
                        </div>
                      </div>
                      <p className="text-xs text-gray-600 mt-2">Feed: 0.05-0.15 mm/rev (small drills), 0.2-0.4 mm/rev (large drills)</p>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Grooving Calculator */}
              <TabsContent value="grooving">
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Minus className="text-[#2E3A6F]" />
                      Grooving & Parting Calculator
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="groove-diameter">Workpiece Diameter (mm)</Label>
                        <Input
                          id="groove-diameter"
                          type="number"
                          placeholder="e.g., 40"
                          value={groovingInputs.diameter}
                          onChange={(e) => setGroovingInputs({...groovingInputs, diameter: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="groove-vc">Cutting Speed Vc (m/min)</Label>
                        <Input
                          id="groove-vc"
                          type="number"
                          placeholder="e.g., 150"
                          value={groovingInputs.cuttingSpeed}
                          onChange={(e) => setGroovingInputs({...groovingInputs, cuttingSpeed: e.target.value})}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="groove-feed">Feed per Rev f (mm/rev)</Label>
                        <Input
                          id="groove-feed"
                          type="number"
                          step="0.01"
                          placeholder="e.g., 0.1"
                          value={groovingInputs.feedPerRev}
                          onChange={(e) => setGroovingInputs({...groovingInputs, feedPerRev: e.target.value})}
                        />
                      </div>
                    </div>

                    <Button onClick={calculateGrooving} className="w-full bg-[#2E3A6F] hover:bg-[#1f2747]">
                      Calculate
                    </Button>

                    {groovingResults && (
                      <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="bg-[#2E3A6F]/5 rounded-lg p-6 space-y-4"
                      >
                        <h3 className="font-semibold text-lg text-[#2E3A6F]">Results:</h3>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                          <div>
                            <p className="text-sm text-gray-600">Spindle Speed (n)</p>
                            <p className="text-2xl font-bold text-[#2E3A6F]">{groovingResults.spindleSpeed} RPM</p>
                          </div>
                          <div>
                            <p className="text-sm text-gray-600">Feed Rate (Vf)</p>
                            <p className="text-2xl font-bold text-[#2E3A6F]">{groovingResults.feedRate} mm/min</p>
                          </div>
                        </div>
                      </motion.div>
                    )}

                    <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm">
                      <p className="font-semibold mb-2">Recommended Cutting Speeds (Vc) for Grooving & Parting:</p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <div>
                          <p className="font-medium text-gray-800">Steel:</p>
                          <ul className="space-y-1 text-gray-700 ml-4">
                            <li>• Low Carbon: 120-180 m/min</li>
                            <li>• Medium Carbon: 100-150 m/min</li>
                            <li>• Stainless: 60-100 m/min</li>
                          </ul>
                        </div>
                        <div>
                          <p className="font-medium text-gray-800">Other Materials:</p>
                          <ul className="space-y-1 text-gray-700 ml-4">
                            <li>• Cast Iron: 100-180 m/min</li>
                            <li>• Aluminum: 250-400 m/min</li>
                            <li>• Brass: 150-250 m/min</li>
                          </ul>
                        </div>
                      </div>
                      <p className="text-xs text-gray-600 mt-2">Feed: 0.05-0.15 mm/rev (narrow inserts), 0.08-0.2 mm/rev (wide inserts). Use lower speeds for deep grooving/parting.</p>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>

            {/* Recommended Cutting Data Reference */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="mt-8"
            >
              <Card className="border-2 border-[#2E3A6F]/20">
                <CardHeader>
                  <CardTitle>Important Notes</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3 text-sm text-gray-700">
                    <p>• Recommended parameters vary based on tool material (HSS vs Carbide), coolant type, machine rigidity, and workpiece condition.</p>
                    <p>• Always start with conservative parameters and increase gradually based on results.</p>
                    <p>• For harder materials or difficult conditions, reduce cutting speed by 20-30%.</p>
                    <p>• Consult tool manufacturer's recommendations for specific insert grades and geometries.</p>
                  </div>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CuttingCalculator;
